import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Alert,
    useTheme,
    useMediaQuery
} from '@mui/material';

import { LocationOn, Close, Refresh, Edit } from '@mui/icons-material';

const LocationPermissionDialog = ({ 
    open, 
    onClose, 
    onRefresh, 
    onManualEntry, 
    gpsAccuracySetting = 'Off' 
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Determine if manual entry should be available based on GPS accuracy setting
    const showManualEntryOption = gpsAccuracySetting === 'Off' || gpsAccuracySetting === 'Manual';
    const isForced = gpsAccuracySetting === 'Forced';

    const getDialogTitle = () => {
        return isForced ? 'Location Required' : 'Enable Location';
    };

    const getMainMessage = () => {
        if (isForced) {
            return 'Location access is required to continue using this service.';
        }
        return 'Please enable location access for accurate service.';
    };

    return (
        <Dialog
            open={open}
            onClose={isForced ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            disableEscapeKeyDown={isForced}
            PaperProps={{
                sx: { 
                    borderRadius: 3,
                    margin: isMobile ? 2 : 3,
                    ...(isForced ? { border: '2px solid #f44336' } : {}),
                    position: 'relative',
                    top: 'auto',
                    left: 'auto',
                    right: 'auto',
                    bottom: 'auto',
                    transform: 'none'
                }
            }}
        >
            <DialogTitle sx={{
                pb: 1,
                pt: 2,
                px: 3,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                ...(isForced ? { backgroundColor: '#ffebee' } : {})
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LocationOn 
                        color={isForced ? "error" : "primary"} 
                        sx={{ fontSize: 24 }}
                    />
                    <Typography 
                        variant="h6" 
                        color={isForced ? "error.main" : "inherit"}
                        sx={{ fontWeight: 600, fontSize: '1.1rem' }}
                    >
                        {getDialogTitle()}
                    </Typography>
                </Box>
                {!isForced && (
                    <IconButton
                        edge="end"
                        onClick={onClose}
                        size="small"
                        sx={{ p: 1 }}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                )}
            </DialogTitle>
            
            <DialogContent sx={{ py: 2, px: 3 }}>
                {/* Main message */}
                <Alert 
                    severity={isForced ? "error" : "info"} 
                    sx={{ mb: 2, borderRadius: 2 }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {getMainMessage()}
                    </Typography>
                </Alert>

                {/* Simplified generic instructions */}
                <Box sx={{ mb: 2 }}>
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontSize: '0.9rem',
                            lineHeight: 1.5,
                            color: 'text.secondary',
                            textAlign: 'center',
                            padding: 2,
                            backgroundColor: 'grey.50',
                            borderRadius: 1
                        }}
                    >
                        Enable location permissions in your browser settings. 
                        If you need help, please contact your administrator.
                    </Typography>
                </Box>

                {/* GPS Accuracy info - only if forced */}
                {isForced && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                            <strong>Required:</strong> GPS accuracy verification is enabled for your account.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{
                px: 3,
                pb: 2.5,
                pt: 1,
                gap: 1.5,
                justifyContent: 'center',
                flexDirection: isMobile ? 'column' : 'row'
            }}>
                {/* Refresh GPS Button */}
                <Button
                    startIcon={<Refresh sx={{ fontSize: 18 }} />}
                    onClick={onRefresh}
                    variant="contained"
                    color={isForced ? "error" : "success"}
                    size="medium"
                    sx={{
                        minWidth: isMobile ? '100%' : 140,
                        height: 44,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        backgroundColor: isForced ? '#f44336' : '#4CAF50',
                        '&:hover': {
                            backgroundColor: isForced ? '#d32f2f' : '#45a049'
                        }
                    }}
                >
                    Refresh
                </Button>

                {/* Manual Entry Button */}
                {showManualEntryOption && (
                    <Button
                        startIcon={<Edit sx={{ fontSize: 18 }} />}
                        onClick={onManualEntry}
                        color="primary"
                        variant="outlined"
                        size="medium"
                        sx={{
                            minWidth: isMobile ? '100%' : 140,
                            height: 44,
                            fontSize: '0.9rem',
                            fontWeight: 500
                        }}
                    >
                        Manual Entry
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default LocationPermissionDialog;